"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createSemanticLedgerLogAction,
  clearSemanticLedgerLogsAction,
} from "@/actions/analytics-actions";
import {
  Search,
  Plus,
  Trash2,
  Cpu,
  User,
  GitCommit,
  Sparkles,
  RefreshCw,
  FolderKanban,
  FileSpreadsheet,
  AlertOctagon,
  Target,
  Timer,
} from "lucide-react";

interface LedgerLog {
  id: string;
  organizationId: string;
  nodeId: string;
  nodeType: string;
  eventType: string;
  description: string;
  authorId: string | null;
  createdAt: string;
}

interface LedgerClientProps {
  initialLogs: LedgerLog[];
}

export function LedgerClient({ initialLogs }: LedgerClientProps) {
  const router = useRouter();
  const [logs, setLogs] = useState<LedgerLog[]>(initialLogs);
  const [search, setSearch] = useState("");
  const [nodeTypeFilter, setNodeTypeFilter] = useState("all");

  // Modal and Simulator State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formNodeType, setFormNodeType] = useState("project");
  const [formEventType, setFormEventType] = useState("Milestone Completed");
  const [formNodeId, setFormNodeId] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formIsAi, setFormIsAi] = useState(true);

  React.useEffect(() => {
    setLogs(initialLogs);
  }, [initialLogs]);

  const handleCreateLog = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formNodeId.trim()) {
      setError("ID Nodo collegato è richiesto.");
      return;
    }
    if (!formDescription.trim()) {
      setError("La descrizione è richiesta.");
      return;
    }

    setLoading(true);
    try {
      const res = await createSemanticLedgerLogAction({
        nodeId: formNodeId.trim(),
        nodeType: formNodeType,
        eventType: formEventType,
        description: formDescription.trim(),
        authorId: formIsAi ? "agent_ai_simulated" : "agent_human_simulated",
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsModalOpen(false);
        setFormNodeId("");
        setFormDescription("");
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante la creazione del log.");
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (
      !confirm(
        "Sei sicuro di voler svuotare il Ledger Semantico per questa organizzazione?",
      )
    )
      return;
    try {
      const res = await clearSemanticLedgerLogsAction();
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante lo svuotamento.");
    }
  };

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.description.toLowerCase().includes(search.toLowerCase()) ||
      log.eventType.toLowerCase().includes(search.toLowerCase()) ||
      log.nodeId.toLowerCase().includes(search.toLowerCase());

    const matchesNodeType =
      nodeTypeFilter === "all" || log.nodeType === nodeTypeFilter;

    return matchesSearch && matchesNodeType;
  });

  const getNodeIcon = (type: string) => {
    switch (type) {
      case "project":
        return <FolderKanban className="h-4 w-4 text-blue-500" />;
      case "invoice":
        return <FileSpreadsheet className="h-4 w-4 text-emerald-500" />;
      case "risk":
        return <AlertOctagon className="h-4 w-4 text-rose-500" />;
      case "goal":
        return <Target className="h-4 w-4 text-purple-500" />;
      case "timesheet":
        return <Timer className="h-4 w-4 text-amber-500" />;
      default:
        return <GitCommit className="h-4 w-4 text-text-muted" />;
    }
  };

  const getNodeBadgeClass = (type: string) => {
    switch (type) {
      case "project":
        return "bg-blue-500/10 text-blue-600 border border-blue-500/20";
      case "invoice":
        return "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20";
      case "risk":
        return "bg-rose-500/10 text-rose-600 border border-rose-500/20";
      case "goal":
        return "bg-purple-500/10 text-purple-600 border border-purple-500/20";
      case "timesheet":
        return "bg-amber-500/10 text-amber-600 border border-amber-500/20";
      default:
        return "bg-card-muted text-text-secondary border border-border";
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
            <Input
              placeholder="Cerca eventi o descrizioni..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>

          <Select
            value={nodeTypeFilter}
            onChange={(e) => setNodeTypeFilter(e.target.value)}
            className="h-9 min-w-[140px]"
          >
            <option value="all">Tutti i nodi</option>
            <option value="project">Progetti (project)</option>
            <option value="invoice">Fatture (invoice)</option>
            <option value="risk">Rischi (risk)</option>
            <option value="goal">Obiettivi (goal)</option>
            <option value="timesheet">Timesheet (timesheet)</option>
          </Select>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearLogs}
            className="text-rose-600 border-rose-500/20 hover:bg-rose-500/5 h-9"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> Pulisci
          </Button>

          <Button
            size="sm"
            onClick={() => setIsModalOpen(true)}
            className="h-9"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Registra Evento
          </Button>
        </div>
      </div>

      {/* Audit Timeline */}
      <Card className="bg-card border border-border shadow-sm">
        <CardContent className="p-6">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-sm text-text-secondary italic">
              Nessun evento registrato nel ledger corrispondente ai filtri.
            </div>
          ) : (
            <div className="relative pl-6 space-y-6 before:absolute before:left-[11px] before:top-2 before:bottom-0 before:w-[1.5px] before:bg-border">
              {filteredLogs.map((log) => {
                const date = new Date(log.createdAt);
                const isAi = log.authorId?.includes("ai") || !log.authorId;

                return (
                  <div key={log.id} className="relative group">
                    {/* Timeline Node Icon/Dot */}
                    <div className="absolute -left-[24px] top-1 bg-card border border-border rounded-full p-0.5 flex items-center justify-center z-10">
                      <span className="w-4 h-4 rounded-full bg-card flex items-center justify-center">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            log.nodeType === "risk"
                              ? "bg-rose-500 animate-pulse"
                              : log.nodeType === "invoice"
                                ? "bg-emerald-500"
                                : log.nodeType === "goal"
                                  ? "bg-purple-500"
                                  : log.nodeType === "project"
                                    ? "bg-blue-500"
                                    : "bg-amber-500"
                          }`}
                        />
                      </span>
                    </div>

                    {/* Timeline Log Content */}
                    <div className="space-y-1.5 pl-3">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="font-semibold text-text-primary bg-card-muted/60 px-2 py-0.5 rounded border border-border/40">
                          {log.eventType}
                        </span>

                        <Badge className={getNodeBadgeClass(log.nodeType)}>
                          <span className="flex items-center gap-1">
                            {getNodeIcon(log.nodeType)}
                            {log.nodeType}
                          </span>
                        </Badge>

                        {/* Actor Badge */}
                        <Badge
                          className={
                            isAi
                              ? "bg-indigo-500/10 text-indigo-600 border border-indigo-500/20"
                              : "bg-teal-500/10 text-teal-600 border border-teal-500/20"
                          }
                        >
                          <span className="flex items-center gap-1">
                            {isAi ? (
                              <Cpu className="h-3 w-3" />
                            ) : (
                              <User className="h-3 w-3" />
                            )}
                            {isAi ? "AI Agent" : "Human Actor"}
                          </span>
                        </Badge>

                        <span className="text-[10px] text-text-secondary ml-auto font-medium">
                          {date.toLocaleString("it-IT", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>

                      <p className="text-xs text-text-primary leading-relaxed font-medium">
                        {log.description}
                      </p>

                      <div className="flex items-center gap-1.5 text-[10px] text-text-secondary">
                        <span className="font-mono">ID Nodo: {log.nodeId}</span>
                        {log.authorId && (
                          <>
                            <span>•</span>
                            <span>Autore: {log.authorId}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Simulator Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-500" /> Registra Evento
              nel Ledger
            </DialogTitle>
            <DialogDescription>
              Simula o immetti un evento manuale all&apos;interno del ledger di
              audit semantico.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateLog} className="space-y-4 my-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="node-type">Tipo Nodo</Label>
                <Select
                  id="node-type"
                  value={formNodeType}
                  onChange={(e) => {
                    setFormNodeType(e.target.value);
                    if (e.target.value === "project")
                      setFormEventType("Milestone Completed");
                    if (e.target.value === "invoice")
                      setFormEventType("Invoice Paid");
                    if (e.target.value === "risk")
                      setFormEventType("Risk Mitigated");
                    if (e.target.value === "goal")
                      setFormEventType("Goal Progress Updated");
                    if (e.target.value === "timesheet")
                      setFormEventType("Hours Logged");
                  }}
                >
                  <option value="project">project (Progetti)</option>
                  <option value="invoice">invoice (Fatturazione)</option>
                  <option value="risk">risk (Rischi & Blocchi)</option>
                  <option value="goal">goal (Obiettivi)</option>
                  <option value="timesheet">timesheet (Timesheet)</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="event-type">Tipo Evento</Label>
                <Input
                  id="event-type"
                  value={formEventType}
                  onChange={(e) => setFormEventType(e.target.value)}
                  placeholder="es. Milestone Completed"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="node-id">ID Nodo di riferimento</Label>
              <Input
                id="node-id"
                value={formNodeId}
                onChange={(e) => setFormNodeId(e.target.value)}
                placeholder="es. proj_redesign_202 o risk_database_leak"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description">
                Descrizione dettagliata dell&apos;evento
              </Label>
              <textarea
                id="description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Inserisci i dettagli di audit dell'evento..."
                className="flex w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 text-foreground min-h-[80px]"
                required
              />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="is-ai"
                checked={formIsAi}
                onChange={(e) => setFormIsAi(e.target.checked)}
                className="rounded border-border text-primary-600 focus:ring-primary-500 h-4 w-4"
              />
              <Label
                htmlFor="is-ai"
                className="cursor-pointer font-normal text-xs text-text-primary"
              >
                Simula come azione intrapresa da un agente AI
              </Label>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                Salva Evento nel Ledger
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
